/**
 * On-Device Reminders — editor (FEAT-REMINDERS-001).
 * Rebuilt to the design canvas (docs/design/local-reminders-prototype.dc.html): a
 * sticky header, a two-column form (numbered step cards) + sticky live-preview aside,
 * all using the ported `rm-` design system (reminders.css). Status honesty throughout:
 * the publish dialog says "Phones pick this up the next time the app opens. Nothing is
 * sent now." — never "Sent"/"Delivered".
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Button, Spinner, Center, useToast, useDisclosure,
} from '@chakra-ui/react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../../components/Header';
import ReminderTypePicker from './ReminderTypePicker';
import ReminderPerformance from './ReminderPerformance';
import ReminderGoal from './ReminderGoal';
import ConditionBuilder from '../../components/goal/ConditionBuilder';
import remindersApi from './api';
import { blankRule, formToApi, apiToForm } from './mappers';
import {
  timingSentence, validate, computeFires, fillTokens, listVars, effVar, builtinsFor,
  previewDefaults, PRAYERS, ISLAMIC, METHODS, SHAFAQ_OPTIONS, CITIES, SAMPLE_EVENTS, SAMPLE_CONTACT,
  DAYS, MON, fTime,
} from './preview/engine';
import './reminders.css';

const WEEKDAYS = [['Mon', 0], ['Tue', 1], ['Wed', 2], ['Thu', 3], ['Fri', 4], ['Sat', 5], ['Sun', 6]];
const SHARED_KEYS = ['id', 'name', 'status', 'audMode', 'segments', 'tags', 'title', 'body', 'tap', 'deeplink', 'vars', 'quiet', 'qStart', 'qEnd', 'maxPerDay', 'priority', 'cancelEvents', 'endDate', 'sample'];
const TYPE_LABEL = { event: 'Event date', schedule: 'Repeating schedule', prayer: 'Prayer & Islamic calendar', inactivity: 'App not opened', first_open: 'Since first open', profile: 'Profile date', milestone: 'App usage milestone' };
const HEADERS = () => ({ 'x-api-key': localStorage.getItem('api-key'), 'x-access-token': localStorage.getItem('customer-portal') });
const cx = (...a) => a.filter(Boolean).join(' ');

// Back-arrow / bell icons etc. kept inline to match the canvas exactly.
const I = {
  back: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 19-7-7 7-7" /><path d="M19 12H5" /></svg>,
  save: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" /><path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7" /><path d="M7 3v4a1 1 0 0 0 1 1h7" /></svg>,
  send: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" /><path d="m21.854 2.147-10.94 10.939" /></svg>,
  clock: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--rm-primary-hover)', flexShrink: 0 }}><circle cx="12" cy="13" r="8" /><path d="M12 9v4l2 2" /></svg>,
  insert: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H7a2 2 0 0 0-2 2v5a2 2 0 0 1-2 2 2 2 0 0 1 2 2v5c0 1.1.9 2 2 2h1" /><path d="M16 21h1a2 2 0 0 0 2-2v-5c0-1.1.9-2 2-2a2 2 0 0 1-2-2V5a2 2 0 0 0-2-2h-1" /></svg>,
  bellSm: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.268 21a2 2 0 0 0 3.464 0" /><path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" /></svg>,
  phoneSm: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="20" x="5" y="2" rx="2" ry="2" /><path d="M12 18h.01" /></svg>,
  x: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>,
  resShow: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></svg>,
  resSkip: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}><circle cx="12" cy="12" r="10" /><path d="m4.9 4.9 14.2 14.2" /></svg>,
  resInfo: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg>,
};

export default function ReminderEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const isNew = !id;
  const initialType = isNew ? searchParams.get('type') : null;

  const [f, setF] = useState(() => {
    if (initialType) { const nf = blankRule(initialType); const m = searchParams.get('mode'); if (m) nf.mode = m; return nf; }
    return blankRule('event');
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [device, setDevice] = useState('android');
  const [cat, setCat] = useState({ events: {}, contact: {}, segments: [], tags: [], eventList: [] });
  const [pv, setPv] = useState(previewDefaults);
  const [menu, setMenu] = useState(null); // 'title' | 'body' | 'link'
  const [advanced, setAdvanced] = useState(false);
  const publishDlg = useDisclosure();
  const picker = useDisclosure({ defaultIsOpen: isNew && !initialType });
  const [changingType, setChangingType] = useState(false);
  const [typeChosen, setTypeChosen] = useState(!isNew || !!initialType);
  const [view, setView] = useState(() => (searchParams.get('view') === 'performance' ? 'performance' : 'settings'));

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setPvK = (k, v) => setPv((p) => ({ ...p, [k]: v }));

  const onPickType = (c) => {
    picker.onClose();
    const nf = blankRule(c.type); if (c.mode) nf.mode = c.mode;
    if (changingType) SHARED_KEYS.forEach((k) => { nf[k] = f[k]; });
    setChangingType(false); setTypeChosen(true); setF(nf);
  };
  const closePicker = () => { picker.onClose(); setChangingType(false); if (!typeChosen) navigate('/content/reminders'); };
  const openChangeType = () => { setChangingType(true); picker.onOpen(); };

  useEffect(() => {
    (async () => {
      try {
        const res = await remindersApi.catalog();
        const d = res.data?.data || {};
        const events = {}; const eventList = [];
        (d.events || []).forEach((ev) => { events[ev.event] = { props: ev.properties || [], channels: ev.channels || [], sdk: ev.sdk_tracked }; eventList.push(ev); });
        const contact = {}; (d.contactAttributes || []).forEach((a) => { contact[a.key] = a.type; });
        if (!eventList.length) Object.keys(SAMPLE_EVENTS).forEach((k) => { events[k] = { props: Object.keys(SAMPLE_EVENTS[k]), channels: ['android app'], sdk: true }; eventList.push({ event: k, sdk_tracked: true }); });
        if (!Object.keys(contact).length) Object.keys(SAMPLE_CONTACT).forEach((k) => { contact[k] = /birthday|_on|_at/.test(k) ? 'date' : 'string'; });
        let tags = [];
        try { const t = await axios.get(`${process.env.REACT_APP_API_URL}/tags`, { headers: HEADERS() }); tags = (t.data?.data || t.data || []).map((x) => x.name || x).filter(Boolean); } catch (e) { /* tags optional */ }
        setCat({ events, contact, segments: d.segments || [], tags, eventList });
      } catch (e) {
        const events = {}; const eventList = [];
        Object.keys(SAMPLE_EVENTS).forEach((k) => { events[k] = { props: Object.keys(SAMPLE_EVENTS[k]), channels: ['android app'], sdk: true }; eventList.push({ event: k, sdk_tracked: true }); });
        const contact = {}; Object.keys(SAMPLE_CONTACT).forEach((k) => { contact[k] = /birthday/.test(k) ? 'date' : 'string'; });
        setCat({ events, contact, segments: [], tags: [], eventList });
      }
      if (!isNew) {
        try { const r = await remindersApi.get(id); setF(apiToForm(r.data.data)); setTypeChosen(true); }
        catch (e) { toast({ title: 'Could not load reminder', status: 'error' }); navigate('/content/reminders'); }
      }
      setLoading(false);
    })();
  }, [id, isNew, navigate, toast]);

  // Prefill the Sample event box when an event is chosen. Real tenant events aren't in
  // SAMPLE_EVENTS, so synthesize a sample from the event's own property names — date-like
  // fields get a tz-qualified ISO so the preview can compute a fire time.
  useEffect(() => {
    if (f.type !== 'event' || !f.event) return;
    const cur = (f.sample || '').trim();
    if (cur && cur !== '{}') return; // never clobber a user-edited sample
    const s = buildSample(f.event, cat, pv.tz);
    if (s && Object.keys(s).length) set('sample', JSON.stringify(s, null, 2));
  }, [f.type, f.event, cat, pv.tz]); // eslint-disable-line

  const liveErrors = useMemo(() => validate(f, cat), [f, cat]);
  const result = useMemo(() => computeFires(f, pv, cat), [f, pv, cat]);
  const vars = useMemo(() => listVars(f), [f]);

  const fire0 = result.fires && result.fires[0];
  const ctx = fire0
    ? { ...(fire0.ctx || {}), fire_date: lockDate(fire0), fire_time: lockTimeStr(fire0) }
    : {};
  const src = { ctx, contact: SAMPLE_CONTACT, sample: (() => { try { return JSON.parse(f.sample); } catch (e) { return SAMPLE_EVENTS[f.event] || {}; } })() };
  const previewTitle = fillTokens(f.title || 'Your reminder title', f, src, cat);
  const previewBody = fillTokens(f.body || 'Your reminder message appears here.', f, src, cat);

  const err = (k) => (showErrors && errors[k]) || '';
  const primaryLabel = f.status === 'paused' ? 'Resume' : f.status === 'active' ? 'Save and update' : 'Publish';
  const statusLabel = f.status === 'active' ? 'Published' : f.status === 'paused' ? 'Paused' : 'Draft';
  const statusCls = f.status === 'active' ? 'rm-published' : f.status === 'paused' ? 'rm-paused' : 'rm-draft';

  const insertToken = (field, name) => { const cur = f[field] || ''; set(field, `${cur}{{${name}}}`); setMenu(null); };
  const insertItems = useMemo(() => {
    const items = [];
    builtinsFor(f.type).forEach((b) => items.push({ name: b, hint: 'Built-in' }));
    if (f.type === 'event' && cat.events[f.event]) (cat.events[f.event].props || []).forEach((p) => items.push({ name: p, hint: 'Event' }));
    Object.keys(cat.contact || {}).forEach((k) => items.push({ name: k, hint: 'Contact' }));
    return items;
  }, [f.type, f.event, cat]);

  const save = async (publishAfter) => {
    const e = validate(f, cat);
    if (publishAfter && Object.keys(e).length) { setErrors(e); setShowErrors(true); toast({ title: 'Fix the highlighted fields', status: 'warning' }); return; }
    setSaving(true);
    try {
      const body = formToApi(f);
      let rid = f.id;
      if (!rid) { const r = await remindersApi.create(body); rid = r.data.data.id; setF((p) => ({ ...p, id: rid })); if (isNew) navigate(`/content/reminders/${rid}`, { replace: true }); }
      else await remindersApi.update(rid, body);
      if (publishAfter) { const act = f.status === 'paused' ? remindersApi.resume : remindersApi.publish; await act(rid); setF((p) => ({ ...p, status: 'active' })); toast({ title: 'Published', status: 'success' }); }
      else toast({ title: 'Draft saved', status: 'success' });
    } catch (err2) {
      const ed = err2?.response?.data; if (ed?.errors) { setErrors(ed.errors); setShowErrors(true); }
      toast({ title: ed?.message || 'Could not save', status: 'error' });
    } finally { setSaving(false); publishDlg.onClose(); }
  };

  if (loading) return (<><Header /><Center h="60vh"><Spinner size="xl" /></Center></>);
  if (!typeChosen) return (<><Header /><ReminderTypePicker isOpen={picker.isOpen} onClose={closePicker} onPick={onPickType} /></>);

  const quietLocked = f.type === 'prayer';

  return (
    <>
      <Header />
      <div className="rm-scope">
        {/* sticky header */}
        <header style={{ position: 'sticky', top: 0, zIndex: 50, display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto minmax(0,1fr)', alignItems: 'center', gap: 16, height: 56, padding: '0 16px', background: 'var(--rm-bg-surface)', borderBottom: '1px solid var(--rm-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <button type="button" className="rm-icon-btn" aria-label="Back to Reminders" style={{ width: 32, height: 32 }} onClick={() => navigate('/content/reminders')}>{I.back}</button>
            <input className="rm-input" value={f.name} onChange={(e) => set('name', e.target.value)} maxLength={80} placeholder="Untitled reminder" style={{ height: 30, width: 280, maxWidth: '100%', padding: '0 8px', borderColor: 'transparent', fontWeight: 600 }} />
            <span className={cx('rm-chip', statusCls)}><span className="rm-dot" />{statusLabel}</span>
          </div>
          <div className="rm-seg" role="tablist" aria-label="Reminder views">
            <button type="button" role="tab" aria-selected={view === 'settings'} className={view === 'settings' ? 'rm-on' : ''} onClick={() => setView('settings')}>Settings</button>
            <button type="button" role="tab" aria-selected={view === 'performance'} className={view === 'performance' ? 'rm-on' : ''} aria-disabled={f.id ? undefined : 'true'} title={f.id ? undefined : 'Save the reminder first'} onClick={() => { if (f.id) setView('performance'); }}>Performance</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" className="rm-btn-secondary" onClick={() => save(false)} disabled={saving}>{I.save} Save draft</button>
            <button type="button" className="rm-btn-primary rm-lg" onClick={() => (Object.keys(liveErrors).length ? (setErrors(liveErrors), setShowErrors(true), toast({ title: 'Fix the highlighted fields', status: 'warning' })) : publishDlg.onOpen())} disabled={saving}>{I.send} {primaryLabel}</button>
          </div>
        </header>

        {view === 'performance' && (
          <div style={{ maxWidth: 1240, margin: '0 auto', padding: 24 }}><ReminderPerformance reminderId={f.id} /></div>
        )}
        <div style={{ maxWidth: 1240, margin: '0 auto', padding: 24, display: view === 'settings' ? 'flex' : 'none', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
          {/* ── form ── */}
          <form onSubmit={(e) => e.preventDefault()} noValidate style={{ flex: '999 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {showErrors && Object.keys(errors).length > 0 && (
              <div className="rm-summary" role="alert">
                <p style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 600 }}>Fix these before publishing</p>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: '22px' }}>
                  {Object.entries(errors).map(([k, v]) => <li key={k}>{v}</li>)}
                </ul>
              </div>
            )}

            {/* 1 · Name and audience */}
            <section className="rm-card">
              <div className="rm-card-h"><span className="rm-step">1</span><h2 className="rm-card-t">Name and audience</h2></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <label className="rm-flabel">Name</label><span className="rm-counter">{(f.name || '').length}/80</span>
                  </div>
                  <input className={cx('rm-input', err('name') && 'rm-bad')} value={f.name} onChange={(e) => set('name', e.target.value)} maxLength={80} />
                  {err('name') && <p className="rm-err">{errors.name}</p>}
                </div>
                <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
                  <legend className="rm-flabel">Audience</legend>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 8 }}>
                    {[['all', 'All users'], ['segments', 'Segments'], ['tags', 'Tags']].map(([v, l]) => (
                      <label key={v} className={cx('rm-aud', f.audMode === v && 'rm-on')}><input type="radio" name="aud" checked={f.audMode === v} onChange={() => set('audMode', v)} /> {l}</label>
                    ))}
                  </div>
                  {f.audMode === 'segments' && <Checklist mt options={cat.segments.map((s) => s.name)} value={f.segments} onChange={(v) => set('segments', v)} empty="No segments found" />}
                  {f.audMode === 'tags' && <Checklist mt options={cat.tags} value={f.tags} onChange={(v) => set('tags', v)} empty="No tags found" />}
                </fieldset>
              </div>
            </section>

            {/* 2 · Message */}
            <section className="rm-card">
              <div className="rm-card-h"><span className="rm-step">2</span><h2 className="rm-card-t">Message</h2></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <FieldWithInsert label="Title" count={`${(f.title || '').length}/60`} open={menu === 'title'} onToggle={() => setMenu(menu === 'title' ? null : 'title')} items={insertItems} onInsert={(n) => insertToken('title', n)}>
                  <input className={cx('rm-input', err('title') && 'rm-bad')} value={f.title} onChange={(e) => set('title', e.target.value)} maxLength={60} />
                  {err('title') && <p className="rm-err">{errors.title}</p>}
                </FieldWithInsert>
                <FieldWithInsert label="Body" count={`${(f.body || '').length}/180`} open={menu === 'body'} onToggle={() => setMenu(menu === 'body' ? null : 'body')} items={insertItems} onInsert={(n) => insertToken('body', n)}>
                  <textarea className="rm-textarea" rows={3} maxLength={180} value={f.body} onChange={(e) => set('body', e.target.value)} />
                </FieldWithInsert>
                <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
                  <legend className="rm-flabel">Tap opens</legend>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <label className="rm-radio"><input type="radio" name="tap" checked={f.tap === 'home'} onChange={() => set('tap', 'home')} /> App home</label>
                    <label className="rm-radio"><input type="radio" name="tap" checked={f.tap === 'link'} onChange={() => set('tap', 'link')} /> Deep link</label>
                  </div>
                  {f.tap === 'link' && (
                    <div style={{ margin: '10px 0 0 26px' }}>
                      <FieldWithInsert label="Deep link" open={menu === 'link'} onToggle={() => setMenu(menu === 'link' ? null : 'link')} items={insertItems} onInsert={(n) => insertToken('deeplink', n)}>
                        <input className="rm-input" value={f.deeplink} onChange={(e) => set('deeplink', e.target.value)} placeholder="app://bundles/{{subscription_id}}" style={{ fontFamily: 'var(--rm-font-mono)', fontSize: 13 }} />
                      </FieldWithInsert>
                    </div>
                  )}
                </fieldset>
                <div>
                  <span className="rm-flabel">Message variables</span>
                  {vars.length > 0 ? (
                    <div style={{ border: '1px solid var(--rm-border)', borderRadius: 'var(--rm-radius-sm)', overflow: 'hidden' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.2fr) 150px', gap: 12, padding: '8px 12px', background: 'var(--rm-bg-subtle)', borderBottom: '1px solid var(--rm-border)', fontSize: 11, fontWeight: 600, color: '#000' }}>
                        <span>Variable</span><span>Value from</span><span>Format</span>
                      </div>
                      {vars.map((v) => <VarRow key={v.name} f={f} v={v} cat={cat} onChange={(m) => set('vars', { ...f.vars, [v.name]: m })} />)}
                    </div>
                  ) : <p className="rm-helper" style={{ marginTop: 0 }}>Your message has no variables yet. Use Insert variable to add one.</p>}
                  {err('vars') && <p className="rm-err" data-testid="err-vars">{errors.vars}</p>}
                </div>
              </div>
            </section>

            {/* 3 · Timing */}
            <section className="rm-card">
              <div className="rm-card-h">
                <span className="rm-step">3</span><h2 className="rm-card-t">Timing</h2>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="rm-chip rm-type">{TYPE_LABEL[f.type]}</span>
                  <button type="button" className="rm-btn-link" onClick={openChangeType}>Change type</button>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <TimingFields f={f} set={set} cat={cat} err={err} advanced={advanced} setAdvanced={setAdvanced} />
                {err('when') && <p className="rm-err" style={{ marginTop: -6 }}>{errors.when}</p>}
                {err('timing') && <p className="rm-err" style={{ marginTop: -6 }}>{errors.timing}</p>}
                <div className="rm-sentence" aria-live="polite">{I.clock}<span>{timingSentence(f, true)}</span></div>
              </div>
            </section>

            {/* 4 · Limits */}
            <section className="rm-card">
              <div className="rm-card-h"><span className="rm-step">4</span><h2 className="rm-card-t">Limits</h2></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                    <label style={{ fontSize: 14, color: 'var(--rm-text-body)' }}>Quiet hours</label>
                    <input type="checkbox" role="switch" className="rm-switch" checked={quietLocked ? false : f.quiet} disabled={quietLocked} onChange={(e) => set('quiet', e.target.checked)} />
                  </div>
                  {quietLocked ? <p className="rm-helper">Off for prayer times. Quiet hours would move or skip Fajr and Isha.</p> : (
                    <>
                      {f.quiet && (
                        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginTop: 12, flexWrap: 'wrap' }}>
                          <div style={{ width: 160 }}><label className="rm-flabel">Start</label><input className="rm-input" type="time" value={f.qStart} onChange={(e) => set('qStart', e.target.value)} /></div>
                          <div style={{ width: 160 }}><label className="rm-flabel">End</label><input className="rm-input" type="time" value={f.qEnd} onChange={(e) => set('qEnd', e.target.value)} /></div>
                          <p style={{ margin: '0 0 9px', fontSize: 14, color: 'var(--rm-text-body)' }}>User's time zone</p>
                        </div>
                      )}
                      <p className="rm-helper">Reminders that land in quiet hours move to the end of the window. If that is after the date, they are skipped.</p>
                    </>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 16 }}>
                  {!quietLocked && (
                    <div><label className="rm-flabel">Max reminders per user per day <span className="rm-opt">(optional)</span></label><input className="rm-input" type="number" min="1" value={f.maxPerDay} onChange={(e) => set('maxPerDay', e.target.value)} /></div>
                  )}
                  <div>
                    <label className="rm-flabel">Priority</label>
                    <select className="rm-select" value={f.priority} onChange={(e) => set('priority', e.target.value)}><option value="high">High</option><option value="normal">Normal</option><option value="low">Low</option></select>
                    <p className="rm-helper">iPhone keeps up to 64 pending notifications per app. Higher priority reminders are kept first.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* 5 · Stop when */}
            <section className="rm-card">
              <div className="rm-card-h"><span className="rm-step">5</span><h2 className="rm-card-t">Stop when</h2></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
                  <legend className="rm-flabel">Cancel when these events arrive</legend>
                  <Checklist mono options={(cat.eventList || []).map((e) => e.event)} value={f.cancelEvents} onChange={(v) => set('cancelEvents', v)} empty="No events found" />
                  <p className="rm-helper">Use your cancellation or expiry events. A renewal is the same trigger event with a new date.</p>
                </fieldset>
                <div style={{ width: 240 }}>
                  <label className="rm-flabel">Stop after <span className="rm-opt">(optional)</span></label>
                  <input className="rm-input" type="date" value={f.endDate} onChange={(e) => set('endDate', e.target.value)} />
                  <p className="rm-helper">No reminders show after this date.</p>
                </div>
              </div>
            </section>

            {/* 6 · Goal (optional, D10) */}
            <ReminderGoal goal={f.goal} onChange={(g) => set('goal', g)} cat={cat} error={err('goal')} />
          </form>

          {/* ── live preview ── */}
          <aside aria-label="Live preview" style={{ flex: '1 1 360px', maxWidth: 400, minWidth: 0, position: 'sticky', top: 72 }}>
            <div style={{ background: 'var(--rm-bg-surface)', border: '1px solid var(--rm-border)', borderRadius: 'var(--rm-radius-md)', padding: '16px 20px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Preview</h2>
                <div className="rm-seg" role="radiogroup" aria-label="Phone type">
                  <button type="button" className={device === 'android' ? 'rm-on' : ''} onClick={() => setDevice('android')}>Android</button>
                  <button type="button" className={device === 'ios' ? 'rm-on' : ''} onClick={() => setDevice('ios')}>iPhone</button>
                </div>
              </div>

              <div className="rm-phone">
                <div style={{ textAlign: 'center', marginBottom: 18 }}>
                  <div style={{ fontSize: 13, opacity: 0.8 }}>{fire0 ? lockDate(fire0) : 'Today'}</div>
                  <div style={{ fontSize: 52, fontWeight: 300, lineHeight: 1.1, letterSpacing: '-1px' }}>{fire0 ? lockTimeStr(fire0) : '—'}</div>
                </div>
                {device === 'android' ? (
                  <div className="rm-n-android">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#4A5568', marginBottom: 4 }}>
                      <span style={{ width: 16, height: 16, borderRadius: '50%', background: 'var(--rm-primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{I.bellSm}</span>
                      <span>Your app</span><span aria-hidden="true">·</span><span>now</span>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.35, wordBreak: 'break-word' }}>{previewTitle}</div>
                    <div style={{ fontSize: 13, lineHeight: 1.4, color: '#4A5568', marginTop: 2, wordBreak: 'break-word' }}>{previewBody}</div>
                  </div>
                ) : (
                  <div className="rm-n-ios">
                    <span style={{ width: 34, height: 34, borderRadius: 8, background: 'var(--rm-primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{I.bellSm}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3, wordBreak: 'break-word' }}>{previewTitle}</span>
                        <span style={{ fontSize: 12, color: '#4A5568', flexShrink: 0 }}>now</span>
                      </div>
                      <div style={{ fontSize: 13, lineHeight: 1.35, marginTop: 1, wordBreak: 'break-word' }}>{previewBody}</div>
                    </div>
                  </div>
                )}
              </div>

              <div className={cx('rm-res', result.kind === 'show' && 'rm-show', result.kind === 'skip' && 'rm-skip', result.kind === 'info' && 'rm-info')} aria-live="polite">
                {result.kind === 'show' ? I.resShow : result.kind === 'skip' ? I.resSkip : I.resInfo}
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, lineHeight: '20px' }}>{result.text}</div>
                  {result.note && <div style={{ fontSize: 12, lineHeight: '18px', marginTop: 2 }}>{result.note}</div>}
                </div>
              </div>

              {result.fires && result.fires.length > 1 && (
                <div>
                  <span className="rm-flabel">Next times</span>
                  <ul className="rm-next">
                    {result.fires.slice(0, 5).map((n, i) => (
                      <li key={i} className={i === 0 ? 'rm-first' : ''}><span>{lockDate(n)}, {lockTimeStr(n)}</span><span>{n.ctx && n.ctx.prayer_name ? n.ctx.prayer_name : ''}</span></li>
                    ))}
                  </ul>
                </div>
              )}
              <p style={{ margin: '-8px 0 0', fontSize: 12, lineHeight: '18px', color: 'var(--rm-text-muted)' }}>Preview uses your device time zone. Phones use each user's own.</p>

              <SampleInput f={f} set={set} pv={pv} setPvK={setPvK} cat={cat} />

              {f.id && (
                <div style={{ borderTop: '1px solid var(--rm-border)', paddingTop: 16 }}>
                  <button type="button" className="rm-btn-secondary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => remindersApi.test(f.id).then((r) => toast({ title: r.data.message || 'Sent to test device', status: 'info' })).catch((e) => toast({ title: e?.response?.data?.message || 'Test failed', status: 'error' }))}>{I.phoneSm} Send to test device</button>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>

      {/* publish confirm */}
      <Modal isOpen={publishDlg.isOpen} onClose={publishDlg.onClose} isCentered>
        <ModalOverlay /><ModalContent borderRadius="14px"><ModalHeader fontSize="17px">{primaryLabel} reminder?</ModalHeader><ModalCloseButton />
          <ModalBody><span style={{ fontSize: 14, color: '#4A5568' }}>Phones pick this up the next time the app opens. Nothing is sent now.</span></ModalBody>
          <ModalFooter gap={2}><Button variant="ghost" onClick={publishDlg.onClose}>Cancel</Button><Button colorScheme="blue" onClick={() => save(true)} isLoading={saving}>{primaryLabel}</Button></ModalFooter>
        </ModalContent>
      </Modal>

      <ReminderTypePicker isOpen={picker.isOpen} onClose={closePicker} onPick={onPickType} changing={changingType} />
    </>
  );
}

// ── sample-event synthesis ──────────────────────────────────────────────────
// A tz-qualified ISO `daysAhead` from now (tz = minutes east of UTC), e.g. 2026-11-07T20:00:00+05:00.
function offsetIso(daysAhead, tz) {
  const shifted = new Date(Date.now() + daysAhead * 86400000 + tz * 60000);
  const p = (n) => String(n).padStart(2, '0');
  const sign = tz >= 0 ? '+' : '-';
  const oh = p(Math.floor(Math.abs(tz) / 60)); const om = p(Math.abs(tz) % 60);
  return `${shifted.getUTCFullYear()}-${p(shifted.getUTCMonth() + 1)}-${p(shifted.getUTCDate())}T${p(shifted.getUTCHours())}:${p(shifted.getUTCMinutes())}:00${sign}${oh}:${om}`;
}
// Build a sample event object for the preview. Prefers the curated demo events; otherwise
// synthesizes one from the event's real property names so the box is never empty.
function buildSample(eventName, cat, tz) {
  if (SAMPLE_EVENTS[eventName]) return SAMPLE_EVENTS[eventName];
  const ev = (cat.events || {})[eventName];
  if (!ev || !ev.props || !ev.props.length) return {};
  const out = {};
  ev.props.forEach((k) => {
    if (/(_at|_on|date|expir|due|deadline|birthday|start|end|renew|valid|time)/i.test(k)) out[k] = offsetIso(7, tz);
    else if (/(amount|total|price|balance|count|qty|quantity|number|(^|_)id$|points?|credit)/i.test(k)) out[k] = '100';
    else out[k] = 'sample';
  });
  return out;
}

// ── lock-screen formatting (from a fire {ms, off}) ──────────────────────────
function lockDate(fire) { const d = new Date(fire.ms + fire.off * 60000); return `${DAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MON[d.getUTCMonth()]}`; }
function lockTimeStr(fire) { const d = new Date(fire.ms + fire.off * 60000); return fTime(d.getUTCHours(), d.getUTCMinutes()); }

// ── small building blocks ───────────────────────────────────────────────────
function FieldWithInsert({ label, count, open, onToggle, items, onInsert, children }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <label className="rm-flabel" style={{ margin: 0 }}>{label}</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative' }}>
          {count && <span className="rm-counter">{count}</span>}
          <button type="button" className="rm-btn-link" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={onToggle}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H7a2 2 0 0 0-2 2v5a2 2 0 0 1-2 2 2 2 0 0 1 2 2v5c0 1.1.9 2 2 2h1" /><path d="M16 21h1a2 2 0 0 0 2-2v-5c0-1.1.9-2 2-2a2 2 0 0 1-2-2V5a2 2 0 0 0-2-2h-1" /></svg>
            Insert variable
          </button>
          {open && (
            <div className="rm-menu" role="menu">
              {items.length === 0 && <div style={{ padding: '7px 12px', fontSize: 13, color: 'var(--rm-text-muted)' }}>Nothing to insert yet.</div>}
              {items.map((m) => <button key={`${m.hint}:${m.name}`} type="button" role="menuitem" onClick={() => onInsert(m.name)}><span className="rm-mono">{`{{${m.name}}}`}</span><span className="rm-hint">{m.hint}</span></button>)}
            </div>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

function Checklist({ options = [], value = [], onChange, empty, mt, mono }) {
  if (!options.length) return <p className="rm-helper" style={{ marginTop: mt ? 10 : 0 }}>{empty}</p>;
  const toggle = (o) => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  return (
    <div className="rm-checklist" style={{ marginTop: mt ? 10 : 0 }}>
      {options.map((o) => (
        <label key={o}><input type="checkbox" checked={value.includes(o)} onChange={() => toggle(o)} /> <span className={mono ? 'rm-mono' : ''} style={{ flex: 1, fontSize: mono ? 13 : 14 }}>{o}</span></label>
      ))}
    </div>
  );
}

function VarRow({ f, v, cat, onChange }) {
  const cur = effVar(f, v.name, cat);
  const dateLike = /date|expires|due|birthday|_at|_on/.test(cur.key);
  const eventProps = (f.type === 'event' && cat.events[f.event] && cat.events[f.event].props) || [];
  const token = '{{' + v.name + '}}';
  const optGroups = [
    <optgroup key="b" label="Built-in">{builtinsFor(f.type).map((b) => <option key={b} value={'b:' + b}>{b}</option>)}</optgroup>,
    eventProps.length > 0 ? <optgroup key="e" label="Event">{eventProps.map((p) => <option key={p} value={'e:' + p}>{p}</option>)}</optgroup> : null,
    <optgroup key="c" label="Contact">{Object.keys(cat.contact || {}).map((k) => <option key={k} value={'c:' + k}>{k}</option>)}</optgroup>,
  ];
  // Keep a saved mapping visible even when the catalog no longer lists it (ANTI-12) — e.g. an
  // identifying contact field the server refuses with errors.vars (DA-BUG-0692).
  const known = optGroups.filter(Boolean).flatMap((g) => React.Children.toArray(g.props.children).map((o) => o.props.value));
  if (cur.key && !known.includes(cur.key)) optGroups.push(<optgroup key="saved" label="Saved"><option value={cur.key}>{cur.key.slice(2)}</option></optgroup>);
  return (
    <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--rm-border-soft)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.2fr) 150px', gap: 12, alignItems: 'center' }}>
        <div style={{ minWidth: 0 }}>
          <span className="rm-mono" style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, background: 'var(--rm-brand-tint)', color: 'var(--rm-brand-ink)', fontSize: 12 }}>{token}</span>
          <div style={{ marginTop: 3, fontSize: 12, color: 'var(--rm-text-muted)' }}>Used in {v.usedIn.join(', ')}</div>
        </div>
        <select className={cx('rm-select', !cur.key && 'rm-bad')} value={cur.key} onChange={(e) => onChange({ key: e.target.value, fmt: cur.fmt })} style={{ fontFamily: 'var(--rm-font-mono)', fontSize: 13 }}>
          <option value="">Pick a value</option>
          {optGroups}
        </select>
        {dateLike ? (
          <select className="rm-select" value={cur.fmt} onChange={(e) => onChange({ key: cur.key, fmt: e.target.value })}><option value="">As sent</option><option value="date">Date</option><option value="time">Time</option><option value="datetime">Date and time</option></select>
        ) : <span style={{ fontSize: 12, color: 'var(--rm-text-muted)' }}>As sent</span>}
      </div>
    </div>
  );
}

// segmented control (seg-split)
function SegSplit({ value, onChange, options, max, labelId }) {
  return (
    <div className="rm-seg-split" role="radiogroup" aria-labelledby={labelId} style={max ? { maxWidth: max } : undefined}>
      {options.map(([v, l]) => <button key={v} type="button" role="radio" aria-checked={value === v} className={value === v ? 'rm-on' : ''} onClick={() => onChange(v)}>{l}</button>)}
    </div>
  );
}
const Num = ({ w = 120, label, bad, ...p }) => (<div style={{ width: w }}><label className="rm-flabel">{label}</label><input className={cx('rm-input', bad && 'rm-bad')} type="number" inputMode="numeric" {...p} /></div>);
const Sel = ({ w = 160, label, children, ...p }) => (<div style={{ width: w }}><label className="rm-flabel">{label}</label><select className="rm-select" {...p}>{children}</select></div>);
const trail = (t) => <p style={{ margin: '0 0 9px', fontSize: 14, color: 'var(--rm-text-body)' }}>{t}</p>;

// ── per-type timing fields ──────────────────────────────────────────────────
function TimingFields({ f, set, cat, err, advanced, setAdvanced }) {
  const props = (f.type === 'event' && cat.events[f.event] && cat.events[f.event].props) || [];
  if (f.type === 'event') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 16 }}>
          <div>
            <label className="rm-flabel">Event</label>
            <select className={cx('rm-select', err('event') && 'rm-bad')} value={f.event} onChange={(e) => set('event', e.target.value)} style={{ fontFamily: 'var(--rm-font-mono)', fontSize: 13 }}>
              <option value="">Pick an event</option>
              {(cat.eventList || []).map((ev) => <option key={ev.event} value={ev.event}>{ev.event}</option>)}
            </select>
            {err('event') && <p className="rm-err" data-testid="err-event">{errMsg(err, 'event')}</p>}
          </div>
          {f.mode !== 'after' && (
            <div>
              <label className="rm-flabel">Date field</label>
              <select className={cx('rm-select', err('dateField') && 'rm-bad')} value={f.dateField} onChange={(e) => set('dateField', e.target.value)} style={{ fontFamily: 'var(--rm-font-mono)', fontSize: 13 }}>
                <option value="">Pick a property</option>
                {props.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              {err('dateField') && <p className="rm-err">{errMsg(err, 'dateField')}</p>}
            </div>
          )}
        </div>
        {f.mode !== 'after' && <p className="rm-helper" style={{ marginTop: -10 }}>The date must include a time zone, like 2026-11-07T20:00:00+05:00. Events without one are skipped.</p>}
        {/* D7-COND (DA-BUG-1422): schedule only for events whose properties match — evaluated on the phone. */}
        <div>
          <span className="rm-flabel">Only when <span className="rm-opt">(optional)</span></span>
          <ConditionBuilder conditions={f.conditions || []} onChange={(c) => set('conditions', c)} propertyOptions={f.event ? props : undefined} propertyLoading={f.event && !cat.events[f.event]} />
          <p className="rm-helper">The reminder is scheduled only when the event&rsquo;s properties match (e.g. <span className="rm-mono">amount &gt; 2000</span>). All conditions must pass.</p>
        </div>
        <div>
          <span className="rm-flabel">Show it</span>
          <SegSplit max={660} value={f.mode === 'tod' ? 'tod' : f.mode} onChange={(v) => set('mode', v)} options={[['before', 'Before the date'], ['tod', 'At a time of day, days before'], ['after', 'After the event happens']]} />
        </div>
        {f.mode === 'after' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <Num label="Number" min="1" value={f.amount} bad={err('when')} onChange={(e) => set('amount', e.target.value)} />
              <Sel label="Unit" value={f.unit} onChange={(e) => set('unit', e.target.value)}><option value="minutes">minutes</option><option value="hours">hours</option><option value="days">days</option></Sel>
              {trail(<>after <span className="rm-mono" style={{ fontSize: 13 }}>{f.event || 'the event'}</span> arrives on the phone</>)}
            </div>
            <div className="rm-note">For unfinished actions like an abandoned checkout. Add the event that completes it under Stop when, so the reminder is cancelled if the user finishes.</div>
          </div>
        )}
        {f.mode === 'before' && (
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Num label="Number" min="1" value={f.amount} bad={err('when')} onChange={(e) => set('amount', e.target.value)} />
            <Sel label="Unit" value={f.unit} onChange={(e) => set('unit', e.target.value)}><option value="minutes">minutes</option><option value="hours">hours</option><option value="days">days</option></Sel>
            {trail(<>before <span className="rm-mono" style={{ fontSize: 13 }}>{f.dateField || 'the date'}</span></>)}
          </div>
        )}
        {f.mode === 'tod' && (
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Num label="Days" min="0" max="30" value={f.days} bad={err('when')} onChange={(e) => set('days', e.target.value)} />
            <div style={{ width: 160 }}><label className="rm-flabel">Time</label><input className="rm-input" type="time" value={f.time} onChange={(e) => set('time', e.target.value)} /></div>
            {trail(<>before <span className="rm-mono" style={{ fontSize: 13 }}>{f.dateField || 'the date'}</span>, in the user's time zone</>)}
          </div>
        )}
        {f.mode !== 'after' && (
          <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
            <legend className="rm-flabel">If the time has already passed</legend>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label className="rm-radio"><input type="radio" name="late" checked={f.late === 'skip'} onChange={() => set('late', 'skip')} /> Skip it</label>
              <label className="rm-radio"><input type="radio" name="late" checked={f.late !== 'skip'} onChange={() => set('late', 'fire_now_if_anchor_future')} /> Show it right away if the date is still ahead</label>
            </div>
          </fieldset>
        )}
        <div style={{ borderTop: '1px solid var(--rm-border-soft)', paddingTop: 14 }}>
          <button type="button" className="rm-btn-link" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13 }} onClick={() => setAdvanced(!advanced)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: advanced ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}><path d="m9 18 6-6-6-6" /></svg>
            Advanced: key and version fields
          </button>
          {advanced && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 16, marginTop: 14 }}>
              <div><label className="rm-flabel">Key field</label><select className="rm-select" value={f.keyField} onChange={(e) => set('keyField', e.target.value)} style={{ fontFamily: 'var(--rm-font-mono)', fontSize: 13 }}><option value="">Auto (first id-like field)</option>{props.map((p) => <option key={p} value={p}>{p}</option>)}</select><p className="rm-helper">One reminder per value. A new event with the same value replaces the reminder.</p></div>
              <div><label className="rm-flabel">Version field <span className="rm-opt">(optional)</span></label><select className="rm-select" value={f.versionField} onChange={(e) => set('versionField', e.target.value)} style={{ fontFamily: 'var(--rm-font-mono)', fontSize: 13 }}><option value="">None</option>{props.map((p) => <option key={p} value={p}>{p}</option>)}</select><p className="rm-helper">Lets phones ignore older events that arrive late.</p></div>
            </div>
          )}
        </div>
      </div>
    );
  }
  if (f.type === 'schedule') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div><span className="rm-flabel">Repeat</span><SegSplit max={360} value={f.repeat} onChange={(v) => set('repeat', v)} options={[['daily', 'Daily'], ['weekly', 'Weekly'], ['monthly', 'Monthly']]} /></div>
        {f.repeat === 'weekly' && (
          <div><span className="rm-flabel">On</span><div className="rm-wk" role="group">{WEEKDAYS.map(([l, n]) => <button key={n} type="button" aria-pressed={f.weekdays.includes(n)} className={f.weekdays.includes(n) ? 'rm-on' : ''} onClick={() => set('weekdays', f.weekdays.includes(n) ? f.weekdays.filter((x) => x !== n) : [...f.weekdays, n])}>{l}</button>)}</div></div>
        )}
        {f.repeat === 'monthly' && (
          <Sel w={200} label="Day of month" value={f.monthDay} onChange={(e) => set('monthDay', e.target.value)}>{[...Array(28)].map((_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}<option value="last">Last day</option></Sel>
        )}
        <div>
          <span className="rm-flabel">At</span>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {(f.times || []).map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input className="rm-input" type="time" value={t} onChange={(e) => { const n = [...f.times]; n[i] = e.target.value; set('times', n); }} style={{ width: 140 }} />
                {f.times.length > 1 && <button type="button" className="rm-icon-btn" style={{ color: 'var(--rm-act-neutral)' }} onClick={() => set('times', f.times.filter((_, j) => j !== i))}>{I.x}</button>}
              </div>
            ))}
            {f.times.length < 6 && <button type="button" className="rm-btn-link" style={{ fontSize: 13 }} onClick={() => set('times', [...f.times, '09:00'])}>+ Add time</button>}
          </div>
          <p className="rm-helper">In the user's time zone.</p>
        </div>
        <div style={{ width: 200 }}><label className="rm-flabel">Start date</label><input className="rm-input" type="date" value={f.startDate} onChange={(e) => set('startDate', e.target.value)} /></div>
      </div>
    );
  }
  if (f.type === 'prayer') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div><span className="rm-flabel">Remind for</span><SegSplit max={360} value={f.pMode} onChange={(v) => set('pMode', v)} options={[['prayers', 'Daily prayers'], ['islamic', 'Islamic dates']]} /></div>
        {f.pMode === 'prayers' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
              <legend className="rm-flabel">Prayers</legend>
              <div className="rm-checks">{PRAYERS.map(([k, l]) => <label key={k} className={f.prayers.includes(k) ? 'rm-on' : ''}><input type="checkbox" checked={f.prayers.includes(k)} onChange={() => set('prayers', f.prayers.includes(k) ? f.prayers.filter((x) => x !== k) : [...f.prayers, k])} /> {l}</label>)}</div>
            </fieldset>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div><span className="rm-flabel">Show it</span><SegSplit value={f.pWhen === 'before' ? 'before' : 'at'} onChange={(v) => set('pWhen', v)} options={[['at', 'At the prayer time'], ['before', 'Minutes before']]} /></div>
              {f.pWhen === 'before' && <Num w={110} label="Minutes" min="1" max="120" value={f.pOffset} bad={err('when')} onChange={(e) => set('pOffset', e.target.value)} />}
            </div>
          </div>
        ) : (
          <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
            <legend className="rm-flabel">Dates</legend>
            <div className="rm-checklist">{ISLAMIC.map(([k, l, desc]) => <label key={k}><input type="checkbox" checked={f.islamic.includes(k)} onChange={() => set('islamic', f.islamic.includes(k) ? f.islamic.filter((x) => x !== k) : [...f.islamic, k])} /> <span style={{ flex: 1 }}>{l}</span><span style={{ fontSize: 12, color: 'var(--rm-text-muted)' }}>{desc}</span></label>)}</div>
          </fieldset>
        )}
        <div style={{ borderTop: '1px solid var(--rm-border-soft)', paddingTop: 16, display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 16 }}>
          <div>
            <label className="rm-flabel" htmlFor="rm-method">Calculation method</label>
            <select id="rm-method" className="rm-select" value={f.method} onChange={(e) => set('method', e.target.value)}>{Object.entries(METHODS).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}</select>
            {METHODS[f.method] && METHODS[f.method].hint && <p className="rm-helper" data-testid="method-hint">{METHODS[f.method].hint}{f.method === 'moonsighting_committee' ? ' The phone applies its seasonal adjustment; the preview here does not.' : ''}</p>}
          </div>
          {f.method === 'moonsighting_committee' && (
            <div data-testid="shafaq-picker"><span className="rm-flabel">Isha twilight (shafaq)</span><SegSplit value={f.shafaq || 'general'} onChange={(v) => set('shafaq', v)} options={SHAFAQ_OPTIONS} /></div>
          )}
          <div><span className="rm-flabel">Asr method</span><SegSplit value={f.asr} onChange={(v) => set('asr', v)} options={[['standard', "Standard (Shafi'i)"], ['hanafi', 'Hanafi']]} /></div>
          <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
            <legend className="rm-flabel">Location</legend>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label className="rm-radio"><input type="radio" name="loc" checked={f.locSource === 'phone'} onChange={() => set('locSource', 'phone')} /> Phone location</label>
              <label className="rm-radio"><input type="radio" name="loc" checked={f.locSource === 'profile'} onChange={() => set('locSource', 'profile')} /> City on the contact profile</label>
            </div>
          </fieldset>
          <div><label className="rm-flabel">Hijri date adjustment</label><select className="rm-select" value={f.hijriAdj} onChange={(e) => set('hijriAdj', e.target.value)}><option value="-1">1 day earlier</option><option value="0">None (Umm al-Qura)</option><option value="1">1 day later</option></select><p className="rm-helper">Moon sighting can differ by a day by country.</p></div>
        </div>
      </div>
    );
  }
  if (f.type === 'inactivity') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div><span className="rm-flabel">Show it when the app</span><SegSplit max={460} value={f.inMode === 'today' ? 'today' : 'days'} onChange={(v) => set('inMode', v)} options={[['days', "Isn't opened for a while"], ['today', "Isn't opened today by a time"]]} /></div>
        {f.inMode !== 'today' ? (
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
            <Num label="Number" min="1" value={f.inAmount} bad={err('when')} onChange={(e) => set('inAmount', e.target.value)} />
            <Sel label="Unit" value={f.inUnit} onChange={(e) => set('inUnit', e.target.value)}><option value="hours">hours</option><option value="days">days</option></Sel>
            {trail('after the last open')}
          </div>
        ) : (
          <div style={{ width: 160 }}><label className="rm-flabel">Not opened by</label><input className={cx('rm-input', err('when') && 'rm-bad')} type="time" value={f.byTime} onChange={(e) => set('byTime', e.target.value)} /></div>
        )}
        <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
          <legend className="rm-flabel">If they still don't open it</legend>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label className="rm-radio"><input type="radio" name="inrep" checked={f.inRepeat === 'once'} onChange={() => set('inRepeat', 'once')} /> Show it once</label>
            <label className="rm-radio"><input type="radio" name="inrep" checked={f.inRepeat === 'repeat'} onChange={() => set('inRepeat', 'repeat')} /> Repeat</label>
          </div>
          {f.inRepeat === 'repeat' && (
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', margin: '10px 0 0 26px', flexWrap: 'wrap' }}>
              {f.inMode !== 'today' && <Num label="Every (days)" min="1" value={f.inEvery} onChange={(e) => set('inEvery', e.target.value)} />}
              <Num label="Up to (times)" min="1" max="10" value={f.inMax} onChange={(e) => set('inMax', e.target.value)} />
            </div>
          )}
        </fieldset>
        <div className="rm-note">The phone sets this when the app closes and moves it each time the app opens. It shows only if the user stays away.</div>
      </div>
    );
  }
  if (f.type === 'first_open') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Num label="Day" min="0" max="90" value={f.foDays} bad={err('when')} onChange={(e) => set('foDays', e.target.value)} />
          <div style={{ width: 160 }}><label className="rm-flabel">At</label><input className="rm-input" type="time" value={f.foTime} onChange={(e) => set('foTime', e.target.value)} /></div>
          {trail('after the app is first opened on the phone')}
        </div>
        <div style={{ width: 240 }}><label className="rm-flabel">Only if first opened after <span className="rm-opt">(optional)</span></label><input className="rm-input" type="date" value={f.foAfter} onChange={(e) => set('foAfter', e.target.value)} /><p className="rm-helper">Leave empty to include existing users.</p></div>
      </div>
    );
  }
  if (f.type === 'profile') {
    const dates = Object.keys(cat.contact || {}).filter((k) => /date|birthday|_on|_at|expiry/.test(k) || cat.contact[k] === 'date');
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ width: 280 }}><label className="rm-flabel" htmlFor="rm-pd-attr">Contact date</label><select id="rm-pd-attr" className={cx('rm-select', err('event') && 'rm-bad')} value={f.attr} onChange={(e) => set('attr', e.target.value)} style={{ fontFamily: 'var(--rm-font-mono)', fontSize: 13 }}>{/* keep a saved value visible even when it is not a date-like field (ANTI-12) */}{f.attr && !(dates.length ? dates : ['birthday']).includes(f.attr) && <option value={f.attr}>{f.attr}</option>}{(dates.length ? dates : ['birthday']).map((n) => <option key={n} value={n}>{n}</option>)}</select>{err('event') ? <p className="rm-err" data-testid="err-event">{errMsg(err, 'event')}</p> : <p className="rm-helper">Synced to the phone from the contact profile.</p>}</div>
        <div><span className="rm-flabel">Show it</span><SegSplit max={320} value={f.pdMode} onChange={(v) => set('pdMode', v)} options={[['on', 'On the day'], ['before', 'Days before']]} /></div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {f.pdMode === 'before' && <Num label="Days" min="1" max="60" value={f.pdAmount} bad={err('when')} onChange={(e) => set('pdAmount', e.target.value)} />}
          <div style={{ width: 160 }}><label className="rm-flabel">At</label><input className="rm-input" type="time" value={f.pdTime} onChange={(e) => set('pdTime', e.target.value)} /></div>
        </div>
        <label className="rm-radio"><input type="checkbox" checked={f.yearly} onChange={(e) => set('yearly', e.target.checked)} /> Repeat every year (birthdays, anniversaries)</label>
      </div>
    );
  }
  if (f.type === 'milestone') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div><span className="rm-flabel">Count</span><SegSplit max={420} value={f.mCount} onChange={(v) => set('mCount', v)} options={[['opens', 'App opens'], ['event', 'Times an event happens']]} /></div>
        {f.mCount === 'event' && (
          <div style={{ maxWidth: 'calc(50% - 8px)' }}><label className="rm-flabel">Event</label><select className={cx('rm-select', err('mEvent') && 'rm-bad')} value={f.mEvent} onChange={(e) => set('mEvent', e.target.value)} style={{ fontFamily: 'var(--rm-font-mono)', fontSize: 13 }}><option value="">Pick an event</option>{(cat.eventList || []).map((ev) => <option key={ev.event} value={ev.event}>{ev.event}</option>)}</select>{err('mEvent') && <p className="rm-err">{errMsg(err, 'mEvent')}</p>}</div>
        )}
        {/* D7-COND (DA-BUG-1422): count only the events whose properties match. */}
        {f.mCount === 'event' && f.mEvent && (
          <div>
            <span className="rm-flabel">Only count when <span className="rm-opt">(optional)</span></span>
            <ConditionBuilder conditions={f.conditions || []} onChange={(c) => set('conditions', c)} propertyOptions={(cat.events[f.mEvent] && cat.events[f.mEvent].props) || []} propertyLoading={!cat.events[f.mEvent]} />
          </div>
        )}
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div><span className="rm-flabel">Show it</span><SegSplit value={f.mRepeat} onChange={(v) => set('mRepeat', v)} options={[['once', 'Once, at the Nth time'], ['every', 'Every Nth time']]} /></div>
          <Num label="N" min="1" max="1000" value={f.mN} bad={err('when')} onChange={(e) => set('mN', e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Num label="Delay" min="0" value={f.mDelay} onChange={(e) => set('mDelay', e.target.value)} />
          <Sel label="Unit" value={f.mDelayUnit} onChange={(e) => set('mDelayUnit', e.target.value)}><option value="minutes">minutes</option><option value="hours">hours</option><option value="days">days</option></Sel>
          {trail("after it's reached")}
        </div>
        <div className="rm-note">Counted on the phone since the app was first opened. A short delay keeps it from showing while the user is still in the app.</div>
      </div>
    );
  }
  return null;
}
function errMsg(err, k) { const v = err(k); return typeof v === 'string' ? v : ''; }

// ── per-type sample input (preview aside) ───────────────────────────────────
function SampleInput({ f, set, pv, setPvK, cat }) {
  if (f.type === 'event') {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <label className="rm-flabel">Sample event</label>
          <button type="button" className="rm-btn-link" onClick={() => set('sample', JSON.stringify(buildSample(f.event, cat, pv.tz), null, 2))}>Reset</button>
        </div>
        <textarea className="rm-textarea" rows={7} spellCheck={false} value={f.sample} onChange={(e) => set('sample', e.target.value)} style={{ fontFamily: 'var(--rm-font-mono)', fontSize: 12, lineHeight: 1.55, background: 'var(--rm-bg-subtle)' }} />
        <p className="rm-helper">Edit values to see how the message and timing change.</p>
      </div>
    );
  }
  if (f.type === 'prayer') return (<div><label className="rm-flabel">Sample city</label><select className="rm-select" value={pv.city} onChange={(e) => setPvK('city', e.target.value)}>{Object.keys(CITIES).map((c) => <option key={c} value={c}>{c}</option>)}</select><p className="rm-helper">Times are calculated on the phone. These are for preview.</p></div>);
  if (f.type === 'inactivity') return (<div><label className="rm-flabel">Sample: last opened</label><input className="rm-input" type="datetime-local" value={pv.lastOpen} onChange={(e) => setPvK('lastOpen', e.target.value)} /></div>);
  if (f.type === 'first_open') return (<div><label className="rm-flabel">Sample: first opened</label><input className="rm-input" type="date" value={pv.firstOpen} onChange={(e) => setPvK('firstOpen', e.target.value)} /></div>);
  if (f.type === 'milestone') return (<div><label className="rm-flabel">Sample: current count</label><input className="rm-input" type="number" min="0" value={pv.mCurrent} onChange={(e) => setPvK('mCurrent', e.target.value)} /><p className="rm-helper">The preview assumes the next one happens now.</p></div>);
  if (f.type === 'profile') return (<div><label className="rm-flabel">Sample: {f.attr || 'date'}</label><input className="rm-input" type="date" value={pv.profileVal} onChange={(e) => setPvK('profileVal', e.target.value)} /></div>);
  return null;
}
